import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import type { MilestoneMap1 } from '@mm-shared/types/MilestoneMap1';
import type { Objective1 } from '@mm-shared/types/Objective1';
import type { DependencyRecord } from '@mm-shared/dependencies';
import { getDependencyId, getDependencyRecords } from '@mm-shared/dependencies';

/**
 * Custom hook that provides comprehensive dependency data for related maps.
 *
 * This hook centralizes the logic for:
 * 1. Getting all dependencies between all related maps (not just main map)
 * 2. Deduplicating dependencies that appear in multiple maps
 * 3. Filtering dependencies based on map visibility
 * 4. Getting visible map UUIDs from Redux state
 *
 * Used by both MapOptionsDropDownMenu and Map3RelativesWrapper to avoid duplication.
 */
export function useComprehensiveDependencies(allRelatedMilestoneMaps: MilestoneMap1[]) {
  // Get Redux data
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);
  const relativesViewSettings = useSelector(
    (state: RootState) => state.map3UI.mapControls.relatives,
  );

  // Get all dependencies between all related maps (comprehensive list)
  const allRelatedMapDependencies = useMemo(() => {
    // Get dependencies from all related maps, not just the main map
    const allDeps: DependencyRecord[] = [];
    allRelatedMilestoneMaps.forEach((map) => {
      const mapDependencies = getDependencyRecords(map, allObjectives, allMaps);
      allDeps.push(...mapDependencies);
    });

    // Remove duplicates (same dependency might be found from multiple maps)
    // TODO: Review. Revise?
    const seenDepIds = new Set<string>();
    return allDeps.filter((dep) => {
      const depId = getDependencyId(dep);
      if (seenDepIds.has(depId)) return false;
      seenDepIds.add(depId);
      return true;
    });
  }, [allRelatedMilestoneMaps, allObjectives, allMaps]);

  // Get currently visible map UUIDs
  const visibleMapUuids = useMemo(() => {
    return new Set([
      ...relativesViewSettings.addedMaps.filter((m) => m.visible).map((m) => m.mapUuid),
      ...relativesViewSettings.relatedToVisibleMaps.filter((m) => m.visible).map((m) => m.mapUuid),
    ]);
  }, [relativesViewSettings.addedMaps, relativesViewSettings.relatedToVisibleMaps]);

  // Filter dependencies based on visibility state (for Map3RelativesWrapper)
  const visibleDependencies = useMemo(() => {
    return allRelatedMapDependencies.filter((dep: DependencyRecord) => {
      const depId = getDependencyId(dep);
      // Check if dependency is visible in the Redux state structure
      return (
        relativesViewSettings.visibleMapsDependencies.find((d) => d.depId === depId)?.visible ||
        false
      );
    });
  }, [allRelatedMapDependencies, relativesViewSettings.visibleMapsDependencies]);

  // Filter dependencies where at least one map is visible (for MapOptionsDropDownMenu)
  const relevantDependencies = useMemo(() => {
    return allRelatedMapDependencies.filter((dep) => {
      // Only show dependencies where at least one of the maps (source or target) is visible
      const sourceMapVisible = visibleMapUuids.has(dep.sourceMap.uuid);
      const targetMapVisible = visibleMapUuids.has(dep.targetMap.uuid);
      return sourceMapVisible || targetMapVisible;
    });
  }, [allRelatedMapDependencies, visibleMapUuids]);

  return {
    // Raw data
    allRelatedMapDependencies,
    visibleMapUuids,
    relativesViewSettings,

    // Filtered data for different use cases
    visibleDependencies, // For Map3RelativesWrapper (based on Redux visibility state)
    relevantDependencies, // For MapOptionsDropDownMenu (based on map visibility)
  };
}
